/**
 * Presenter-side control for demo voting. Lives in the shell's sidebar
 * footer: one wide dialog with the QR on the left (constant) and, on the right,
 * a reveal gate — a live vote counter until the presenter hits "Show results",
 * then the full results panel (distribution + KPIs + themed clusters).
 *
 * Polling is scoped to what's on screen: the summary polls whenever the dialog
 * is open (the hidden counter and the revealed distribution both stay live);
 * themes poll only once results are revealed.
 */

import { Vote } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { type ReactNode, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useVoteSummary, useVoteThemes } from "@/lib/api";

import { VoteResults } from "./vote-results";

const POLL_MS = 10000;

/** Full label-and-icon trigger for the expanded footer variant. */
export function PresenterVotingDialog() {
  return (
    <PresenterVotingDialogBase>
      <button
        type="button"
        className="text-foreground hover:bg-warm-gray-02 flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors"
      >
        <Vote className="h-4 w-4 shrink-0" aria-hidden />
        Audience voting
      </button>
    </PresenterVotingDialogBase>
  );
}

/** Icon-only trigger for the collapsed footer variant, matching the other
 *  collapsed rail buttons (profile / sign out). */
export function PresenterVotingDialogCollapsed() {
  return (
    <PresenterVotingDialogBase>
      <button
        type="button"
        aria-label="Audience voting"
        className="text-primary-foreground flex h-10 w-full items-center justify-center rounded-md transition-colors hover:bg-white/10"
      >
        <Vote className="h-4 w-4" />
      </button>
    </PresenterVotingDialogBase>
  );
}

function PresenterVotingDialogBase({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [revealed, setRevealed] = useState(false);

  // Summary polls while open (both states need the live count); themes only
  // once revealed. `enabled` gates the poll, so a plain interval is enough.
  const summaryQuery = useVoteSummary({ enabled: open, refetchInterval: POLL_MS });
  const themesQuery = useVoteThemes({ enabled: open && revealed, refetchInterval: POLL_MS });

  const summary = summaryQuery.data;
  const themes = themesQuery.data;
  const total = summary?.total ?? 0;

  const voteUrl = `${window.location.origin}/vote`;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Re-arm the reveal gate on every close: revealing is a deliberate
        // presenter action, so each open should start hidden.
        if (!next) {
          setRevealed(false);
        }
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-heading">Audience voting</DialogTitle>
          <DialogDescription>
            Attendees scan to rate this demo. Reveal the results when you&apos;re ready to share
            them with the room.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-6 md:flex-row">
          {/* Left: the QR, constant across both states. */}
          <div className="flex flex-col items-center gap-3 md:w-[280px] md:shrink-0">
            <div className="border-border rounded-lg border bg-white p-4">
              <QRCodeSVG value={voteUrl} size={240} bgColor="#ffffff" fgColor="#141414" level="M" />
            </div>
            <span className="text-muted text-sm break-all select-all">{voteUrl}</span>
          </div>

          {/* Right: reveal gate → results. */}
          <div className="min-w-0 flex-1">
            {revealed ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-foreground text-base font-bold">Live results</h3>
                  <button
                    type="button"
                    onClick={() => setRevealed(false)}
                    className="text-muted hover:text-foreground text-xs font-medium transition-colors"
                  >
                    Hide results
                  </button>
                </div>
                <VoteResults
                  summary={summary}
                  themes={themes}
                  themesLoading={themesQuery.isLoading}
                />
              </div>
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-5 py-8 text-center">
                <div className="space-y-1">
                  <h3 className="font-heading text-foreground text-xl font-bold">
                    Results are hidden
                  </h3>
                  <p className="text-muted text-sm">
                    Reveal when you&apos;re ready to share the room&apos;s feedback.
                  </p>
                </div>
                <p className="text-foreground text-sm font-medium">
                  {summary ? `${total} votes so far` : "Waiting for votes…"}
                </p>
                <Button
                  type="button"
                  onClick={() => setRevealed(true)}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Show results
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
